/**
 * Jira Markdown Renderer - Styles markdown responses from Jira RAG backend
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useTheme } from '../Theme/ThemeProvider';
import { DownloadIcon } from '@radix-ui/react-icons';
import { useDispatch } from 'react-redux';
import { openJiraCard } from '../../store/chatSlice';
import PlotlyChartViewer from './PlotlyChartViewer';
import './JiraMarkdown.css';

interface JiraMarkdownProps {
  content: string;
  ui_events?: any[]; // Optional UI events array from Jira response
  onChartImagesReady?: (images: string[]) => void;
}

/**
 * Component to render Jira markdown responses with custom styling
 * Handles two rendering modes:
 * 1. Regular markdown answers (when no ui_events) - uses Navigator's standard table styling
 * 2. Card rendering with special HTML styling (when ui_events contains HTML)
 */
export const JiraMarkdown: React.FC<JiraMarkdownProps> = ({ content, ui_events, onChartImagesReady }) => {
  const dispatch = useDispatch();
  const { mode } = useTheme();
  // Check if we have a card to render (ui_events with HTML)
  const hasCard = ui_events && ui_events.length > 0 && ui_events[0].html;
  // Collect plotly chart events
  const chartEvents = ui_events?.filter(
    (e: any) => e.component === 'plotly_chart' && e.payload?.data
  ) ?? [];

  // Capture chart PNG images and surface them to the parent via onChartImagesReady
  const capturedImagesRef = React.useRef<string[]>([]);
  const handleChartImageCaptured = React.useCallback((chartIndex: number, dataUrl: string) => {
    capturedImagesRef.current[chartIndex] = dataUrl;
    const filled = capturedImagesRef.current.filter(Boolean);
    if (onChartImagesReady && filled.length === chartEvents.length && chartEvents.length > 0) {
      onChartImagesReady([...capturedImagesRef.current]);
    }
  }, [chartEvents.length, onChartImagesReady]);
  
  // Themed table components - same as MessageContentRenderer for consistent styling
  const ThemedTable: React.FC<{children: React.ReactNode}> = ({children}) => {
    const { mode } = useTheme();
    const isDark = mode === 'dark';
    const tableRef = React.useRef<HTMLTableElement>(null);
    
    const handleDownloadCSV = () => {
      if (!tableRef.current) return;
      
      const table = tableRef.current;
      const rows: string[][] = [];
      
      // Extract headers
      const headers = Array.from(table.querySelectorAll('thead th')).map(th => {
        const text = th.textContent || '';
        return text.includes(',') || text.includes('"') || text.includes('\n') 
          ? `"${text.replace(/"/g, '""')}"` 
          : text;
      });
      if (headers.length > 0) rows.push(headers);
      
      // Extract body rows
      table.querySelectorAll('tbody tr').forEach(tr => {
        const cells = Array.from(tr.querySelectorAll('td')).map(td => {
          const text = td.textContent || '';
          return text.includes(',') || text.includes('"') || text.includes('\n')
            ? `"${text.replace(/"/g, '""')}"` 
            : text;
        });
        if (cells.length > 0) rows.push(cells);
      });
      
      // Create CSV content
      const csvContent = rows.map(row => row.join(',')).join('\n');
      
      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `table_${Date.now()}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };
    
    return (
      <div style={{marginBottom: '1.5em', marginTop: '1em'}}>
        <div style={{overflowX: 'auto'}}>
          <table 
            ref={tableRef}
            style={{
              borderCollapse: 'collapse',
              width: '100%',
              border: `1px solid ${isDark ? '#444' : '#d1d5db'}`,
              fontSize: '0.9em'
            }}>
            {children}
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5em' }}>
          <button
            onClick={handleDownloadCSV}
            style={{
              padding: '0.5em',
              fontSize: '0.85em',
              backgroundColor: 'var(--color-secondary)',
              color: 'var(--color-text)',
              border: `1px solid ${isDark ? '#444' : '#d1d5db'}`,
              borderRadius: '0.375rem',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s, border-color 0.2s',
              fontFamily: 'inherit'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? '#424248' : '#e5e7eb';
              e.currentTarget.style.borderColor = isDark ? '#555' : '#9ca3af';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-secondary)';
              e.currentTarget.style.borderColor = isDark ? '#444' : '#d1d5db';
            }}
            title="Download table as CSV"
            aria-label="Download table as CSV"
          >
            <DownloadIcon width={16} height={16} style={{ display: 'block' }} />
          </button>
        </div>
      </div>
    );
  };

  const ThemedThead: React.FC<{children: React.ReactNode}> = ({children}) => {
    const { mode } = useTheme();
    const isDark = mode === 'dark';
    return (
      <thead style={{
        backgroundColor: 'var(--form-accent)',
        borderBottom: `2px solid ${isDark ? '#555' : '#9ca3af'}`
      }}>
        {children}
      </thead>
    );
  };

  const ThemedTr: React.FC<{children: React.ReactNode}> = ({children}) => {
    const { mode } = useTheme();
    const isDark = mode === 'dark';
    return (
      <tr style={{
        borderBottom: `1px solid ${isDark ? '#444' : '#e5e7eb'}`
      }}>
        {children}
      </tr>
    );
  };

  const ThemedTh: React.FC<{children: React.ReactNode}> = ({children}) => {
    const { mode } = useTheme();
    const isDark = mode === 'dark';
    return (
      <th style={{
        padding: '0.75em 1em',
        textAlign: 'left',
        fontWeight: 'bold',
        backgroundColor: 'transparent',
        borderRight: `1px solid ${isDark ? '#444' : '#d1d5db'}`
      }}>
        {children}
      </th>
    );
  };

  const ThemedTd: React.FC<{children: React.ReactNode}> = ({children}) => {
    const { mode } = useTheme();
    const isDark = mode === 'dark';
    return (
      <td style={{
        padding: '0.75em 1em',
        backgroundColor: 'transparent',
        borderRight: `1px solid ${isDark ? '#444' : '#e5e7eb'}`
      }}>
        {children}
      </td>
    );
  };

  // Custom link component - handle non-functional references (anchor-only URLs)
  const MarkdownLink: React.FC<any> = (props) => {
    const { href, children } = props;
    
    // Check if href is a non-functional reference (empty, just #, or anchor-only like #ref-1)
    // Keep as link for consistent styling, but prevent navigation
    const isNonFunctionalRef = !href || href.trim() === '' || href.trim() === '#' || /^#[a-zA-Z0-9_-]*$/.test(href.trim());
    
    return (
      <a
        href={href}
        onClick={(e) => {
          if (isNonFunctionalRef) {
            e.preventDefault();
          }
        }}
        target={isNonFunctionalRef ? undefined : '_blank'}
        rel={isNonFunctionalRef ? undefined : 'noopener noreferrer'}
        className="text-blue-600 hover:underline"
        style={{
          fontWeight: 500,
          cursor: 'pointer'
        }}
      >
        {children}
      </a>
    );
  };

  // Fullscreen button click handler
  const handleFullscreenClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasCard && ui_events[0].html) {
      dispatch(openJiraCard(ui_events[0].html));
    }
  }, [dispatch, hasCard, ui_events]);
  
  return (
    <div className="jira-markdown-container">
      {/* Render card if ui_events exists, otherwise render regular answer */}
      {hasCard ? (
        <div
          className="jira-card-content"
          data-mode={mode}
          style={{ position: 'relative' }}
        >
          {/* Fullscreen button always visible */}
          <button
            className="jira-card-fullscreen-btn"
            style={{
              position: 'absolute',
              top: 48,
              right: 16,
              zIndex: 20,
              background: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              padding: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              transition: 'background-color 0.2s ease, opacity 0.2s ease',
              opacity: 0.7,
            }}
            title="Open in fullscreen"
            onClick={handleFullscreenClick}
          >
            {/* SVG icon for fullscreen */}
            <svg width="20" height="20" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1H5V2H2V5H1V1Z" fill="white"/>
              <path d="M10 1V2H13V5H14V1H10Z" fill="white"/>
              <path d="M1 10V14H5V13H2V10H1Z" fill="white"/>
              <path d="M14 10H13V13H10V14H14V10Z" fill="white"/>
            </svg>
          </button>
          {/* Card HTML content */}
          <div
            style={{ position: 'relative' }}
            dangerouslySetInnerHTML={{ __html: ui_events[0].html }}
          />
          {/* Plotly charts below the card */}
          {chartEvents.map((event: any, i: number) => (
            <PlotlyChartViewer key={i} payload={event.payload} onImageCaptured={(dataUrl) => handleChartImageCaptured(i, dataUrl)} />
          ))}
        </div>
      ) : (
        <div className="jira-answer-content">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              // Use Navigator's standard themed table components for consistent styling
              table: ({children}) => <ThemedTable>{children}</ThemedTable>,
              thead: ({children}) => <ThemedThead>{children}</ThemedThead>,
              tbody: ({children}) => <tbody>{children}</tbody>,
              tr: ({children}) => <ThemedTr>{children}</ThemedTr>,
              th: ({children}) => <ThemedTh>{children}</ThemedTh>,
              td: ({children}) => <ThemedTd>{children}</ThemedTd>,
              // Standard text elements use Navigator's color scheme
              h2: ({children, node}) => <h2 style={{color: 'var(--color-text)', fontSize: '1.2em', fontWeight: 'bold', marginTop: (node as any)?.position?.start?.line === 1 ? 0 : '1.5em', marginBottom: '0.75em'}}>{children}</h2>,
              ul: ({children}) => <ul style={{
                marginLeft: '1.25em',
                marginBottom: '1.25em',
                paddingTop: '0.25em',
                listStyleType: 'disc',
                paddingLeft: '0.75em'
              }}>{children}</ul>,
              ol: ({children}) => <ol style={{
                marginLeft: '1.25em',
                marginBottom: '1.25em',
                paddingTop: '0.25em',
                listStyleType: 'decimal',
                paddingLeft: '0.75em'
              }}>{children}</ol>,
              li: ({children}) => <li style={{
                marginBottom: '0.4em',
                paddingBottom: '0.1em',
                color: 'var(--color-text)',
                lineHeight: '1.55'
              }}>{children}</li>,
              p: ({children}) => <p style={{marginBottom: '0.75em', color: 'var(--color-text)'}}>{children}</p>,
              strong: ({children}) => <strong style={{color: 'var(--color-text)', fontWeight: 'bold'}}>{children}</strong>,
              a: MarkdownLink as any,
            }}
          >
            {content}
          </ReactMarkdown>
          {/* Plotly charts below the markdown answer */}
          {chartEvents.map((event: any, i: number) => (
            <PlotlyChartViewer key={i} payload={event.payload} onImageCaptured={(dataUrl) => handleChartImageCaptured(i, dataUrl)} />
          ))}
        </div>
      )}
    </div>
  );
};

export default JiraMarkdown;
